export function bulkTableCellKey(rowId: string, colKey: string): string {
  return `${rowId}:${colKey}`;
}

export function parseBulkTableCellKey(key: string): { rowId: string; colKey: string } | null {
  const sep = key.indexOf(':');
  if (sep <= 0) return null;
  return { rowId: key.slice(0, sep), colKey: key.slice(sep + 1) };
}

export function rectBulkTableCellKeys(
  orderedRows: Array<{ id: string }>,
  columnKeys: string[],
  anchor: { rowIdx: number; colKey: string },
  current: { rowIdx: number; colKey: string }
): string[] {
  const aCol = columnKeys.indexOf(anchor.colKey);
  const cCol = columnKeys.indexOf(current.colKey);
  if (aCol < 0 || cCol < 0) return [];
  const minR = Math.min(anchor.rowIdx, current.rowIdx);
  const maxR = Math.max(anchor.rowIdx, current.rowIdx);
  const minC = Math.min(aCol, cCol);
  const maxC = Math.max(aCol, cCol);
  const keys: string[] = [];
  for (let r = minR; r <= maxR; r += 1) {
    const row = orderedRows[r];
    if (!row) continue;
    for (let c = minC; c <= maxC; c += 1) {
      keys.push(bulkTableCellKey(row.id, columnKeys[c]));
    }
  }
  return keys;
}

export function bulkTableCellNeighborKeys(
  orderedRows: Array<{ id: string }>,
  columnKeys: string[],
  rowIdx: number,
  colKey: string
): { top: string | null; bottom: string | null; left: string | null; right: string | null } {
  const row = orderedRows[rowIdx];
  if (!row) return { top: null, bottom: null, left: null, right: null };
  const colIdx = columnKeys.indexOf(colKey);
  const topRow = orderedRows[rowIdx - 1];
  const bottomRow = orderedRows[rowIdx + 1];
  return {
    top: topRow ? bulkTableCellKey(topRow.id, colKey) : null,
    bottom: bottomRow ? bulkTableCellKey(bottomRow.id, colKey) : null,
    left: colIdx > 0 ? bulkTableCellKey(row.id, columnKeys[colIdx - 1]) : null,
    right:
      colIdx >= 0 && colIdx < columnKeys.length - 1
        ? bulkTableCellKey(row.id, columnKeys[colIdx + 1])
        : null,
  };
}

export function shouldHandleBulkTablePaste(text: string): boolean {
  if (text.includes('\t')) return true;
  const lines = text.split(/\r\n|\n|\r/).filter((line) => line.trim());
  return lines.length >= 2;
}

export function parseBulkTablePasteGrid(raw: string): string[][] {
  const text = raw.replace(/^\uFEFF/, '');
  const lines = text.split(/\r\n|\n|\r/);
  const grid = lines.map((line) => line.split('\t').map((cell) => cell.replace(/\r$/, '')));
  while (grid.length && grid[grid.length - 1].every((cell) => !(cell ?? '').trim())) {
    grid.pop();
  }
  return grid;
}

/** 복수 셀 선택 시 해당 행의 모든 셀을 선택 범위에 포함 */
export function expandBulkSelectionToFullRows(
  cellKeys: string[],
  orderedRows: Array<{ id: string }>,
  columnKeys: string[]
): string[] {
  if (cellKeys.length <= 1) return cellKeys;
  const rowIds = new Set<string>();
  for (const key of cellKeys) {
    const parsed = parseBulkTableCellKey(key);
    if (parsed) rowIds.add(parsed.rowId);
  }
  const expanded: string[] = [];
  for (const row of orderedRows) {
    if (!rowIds.has(row.id)) continue;
    for (const colKey of columnKeys) {
      expanded.push(bulkTableCellKey(row.id, colKey));
    }
  }
  return expanded;
}
